import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetCatalogQuery } from '../../../redux/api/fymAPI';
import { useAccountId } from '../../../redux/slices/appInfo/hooks';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';
import { catalogArtworkSrc } from '../../smartLinks/catalog/catalogArtwork';
import { TypedMemo } from '../../common/types';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../common/views/LoadingView';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { catalogAddSongPath } from '../../smartLinks/catalog/addSong/catalogAddSongPath';
import { carryStep } from './carriedDoc';
import { coverCarryMessages } from './coverCarryMessages';
import { useCarriedDesign } from './useCarriedDesign';
import { usePutCarriedDesign } from './usePutCarriedDesign';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import editorStyles from '../CoverEditor.module.scss';
import styles from '../publicMode/PublicCoverMaker.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "Which song is this cover for?" (design cover-editor g4): the page after a sign-in with a design
 * of the public tool. A pick makes the design a new version of the song and opens its editor. With
 * no song yet, "Add a release" comes first: it opens the step-by-step "Add a song" flow (design
 * catalog-add-song), whose cover step sends the new song back here as `?item=<uuid>`. Then the
 * design goes on that song at once. With nothing to carry, Home opens.
 */
const CoverCarryViewComponent: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const accountId = useAccountId();
  const carried = useCarriedDesign();
  const catalog = useGetCatalogQuery({ accountId: accountId ?? 0 }, { skip: accountId === null });
  const { put, busyUuid, isFailed } = usePutCarriedDesign(accountId ?? 0, carried);
  const [params] = useSearchParams();
  const backItemUuid = params.get('item') ?? '';
  const hasPutBackItem = useRef(false);
  const songs = useMemo(() => catalog.data?.items ?? [], [catalog.data]);
  const step = carried === undefined ? null : carryStep({ hasDesign: carried !== null, songCount: songs.length });

  useEffect(() => {
    if (step === 'home') {
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(`/${SMLRouteNames.home}`, { replace: true });
    }
  }, [navigate, step]);
  // Back from the cover step of "Add a song": the design goes on the new song once.
  useEffect(() => {
    if (backItemUuid === '' || hasPutBackItem.current || carried === undefined || carried === null) {
      return;
    }
    const song = songs.find((one) => one.uuid === backItemUuid);
    if (song === undefined) {
      return;
    }
    hasPutBackItem.current = true;
    put(song).catch(console.error);
  }, [backItemUuid, carried, put, songs]);

  const openAdd = useCallback(() => {
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(catalogAddSongPath({ from: 'coverMaker', accountId }));
  }, [accountId, navigate]);

  if (catalog.error !== undefined) {
    return (
      <div className={editorStyles.fullState}>
        <ErrorView error={catalog.error} retry={() => catalog.refetch().catch(console.error)} />
      </div>
    );
  }
  if (accountId === null || carried === undefined || catalog.data === undefined || step === 'home') {
    return (
      <div className={editorStyles.fullState} role="status">
        <LoadingView />
      </div>
    );
  }
  return (
    <div className={styles.carry}>
      <div className={styles.carryCard}>
        <h1>{intl.formatMessage(coverCarryMessages.title)}</h1>
        <p>{intl.formatMessage(step === 'addSong' ? coverCarryMessages.textNoSong : coverCarryMessages.text)}</p>
        <ul className={styles.songList}>
          {songs.map((song) => {
            const cover = catalogArtworkSrc(song, 48) ?? null;
            return (
              <li key={song.uuid}>
                <button
                  type="button"
                  className={styles.songRow}
                  disabled={busyUuid !== null}
                  onClick={() => {
                    put(song).catch(console.error);
                  }}
                >
                  {cover === null ? <span /> : <img src={cover} alt="" />}
                  <span>
                    {song.title}
                    <small>{song.artistName}</small>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {isFailed && (
          <p className={styles.carryError} role="alert">
            {intl.formatMessage(coverCarryMessages.failed)}
          </p>
        )}
        <div className={styles.carryActions}>
          <SMLButton size="sm" outline={step !== 'addSong'} omitCap onClick={openAdd} disabled={busyUuid !== null}>
            {intl.formatMessage(coverCarryMessages.addRelease)}
          </SMLButton>
          {busyUuid !== null && (
            <span className={editorStyles.hint}>{intl.formatMessage(coverCarryMessages.putting)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export const CoverCarryView = TypedMemo(CoverCarryViewComponent);
